import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { VideoRecorderComponent } from './video-recorder/video-recorder.component';
import { VideoPlayerComponent } from './video-player/video-player.component';

export enum CurrentState {
  Guidelines,
  Permissions,
  Record,
  Review,
  ChooseDevices
}

@Component({
  selector: 'suzy-action-forms-open-ended-video',
  templateUrl: './open-ended-video.component.html',
  styleUrls: ['./open-ended-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpenEndedVideoComponent implements OnDestroy {
  @Input() isErrorMessage: string;
  @Input() action;
  @Input() isSuccess: boolean;
  @Input() isLoading: boolean;
  @Input() isSpecialMission: boolean;
  @Input() isValid: boolean;
  @Input() disableSkip: boolean;
  @Input() disableButton: boolean = false;
  @Input() isSkipping: boolean;
  @Input() isSubmitting: boolean;
  @Input() isBannedOrDisabled: boolean;
  @Input() question: string;
  @Input() isPreviewApp: boolean;

  @Output() submitClick = new EventEmitter();
  @Output() formMouseMove = new EventEmitter();
  @Output() formSkip = new EventEmitter();
  @Output() formSubmit = new EventEmitter();

  @ViewChild('reviewVideoPlayer', { static: false })
  reviewVideoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('liveCamera', { static: false })
  liveCameraElement!: ElementRef<HTMLVideoElement>;

  downloadUrl: string = '';
  videoStream: MediaStream;
  blob: Blob | null = null;

  CurrentState = CurrentState; // allows enum to be used in html/template

  currentState: CurrentState = CurrentState.Guidelines;
  hasAgreedToGuidelines = false;
  hasGivenGuidelinesAnswer = false;
  hasDetectedMicrophone = false;

  // New: Device and permission state
  availableVideoDevices: MediaDeviceInfo[] = [];
  availableAudioDevices: MediaDeviceInfo[] = [];
  selectedVideoDeviceId: string = '';
  selectedAudioDeviceId: string = '';
  hasMediaPermissions: boolean = false;

  audioLevel: number = 0; // 0 to 1
  isReadyToSubmit = false;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private audioStream: MediaStream | null = null;
  private audioLevelAnimationFrame: number | null = null;

  isReviewVideoPlaying = false;

  //errors
  guidelinesError: string | null = null;
  cameraError: string | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private translate: TranslateService,
    private modalService: NgbModal
  ) {}

  ngOnDestroy(): void {
    this.stopVideoStream();
    this.stopAudioStream();
  }

  onGuidelinesRespond(
    hasAgreed: boolean,
    hasGuidelinesAnswered: boolean
  ): void {
    this.hasAgreedToGuidelines = hasAgreed;
    this.hasGivenGuidelinesAnswer = hasGuidelinesAnswered;

    // Show error immediately if user disagrees
    if (hasGuidelinesAnswered && !hasAgreed) {
      this.guidelinesError = this.translate.instant(
        'videoOpenEnded.guidelinesShouldBeAgreed'
      );
    } else {
      // remove error message if it was shown
      this.guidelinesError = null;
    }
  }

  stopAudioStream() {
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.audioLevelAnimationFrame) {
      cancelAnimationFrame(this.audioLevelAnimationFrame);
      this.audioLevelAnimationFrame = null;
    }
  }

  // Request permissions and load devices
  async requestMediaPermissions() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      this.hasMediaPermissions = true;
      // Stop the stream immediately after getting permission to avoid camera light staying on
      stream.getTracks().forEach(track => track.stop());
      await this.loadDevices();
      this.currentState = CurrentState.ChooseDevices;
      this.onVideoDeviceChange();
      this.onAudioDeviceChange();
    } catch (e) {
      this.hasMediaPermissions = false;
      alert('Permission denied. Please allow access to camera and microphone.');
    }

    this.cdr.detectChanges(); // Add this line
  }

  // Load available devices
  async loadDevices() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    this.availableVideoDevices = devices.filter(d => d.kind === 'videoinput');
    this.availableAudioDevices = devices.filter(d => d.kind === 'audioinput');
    // Set defaults if available
    if (this.availableVideoDevices.length > 0) {
      this.selectedVideoDeviceId = this.availableVideoDevices[0].deviceId;
    }
    if (this.availableAudioDevices.length > 0) {
      this.selectedAudioDeviceId = this.availableAudioDevices[0].deviceId;
    }
  }

  async onVideoDeviceChange() {
    if (this.selectedVideoDeviceId) {
      this.stopVideoStream();
      try {
        this.videoStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: this.selectedVideoDeviceId } },
          audio: false
        });
        if (this.liveCameraElement && this.liveCameraElement.nativeElement) {
          this.liveCameraElement.nativeElement.srcObject = this.videoStream;
          this.liveCameraElement.nativeElement.play();
        }
      } catch (e) {
        this.cameraError = this.translate.instant(
          'videoOpenEnded.cameraNotDetected'
        );
      }
    }
  }

  async onAudioDeviceChange() {
    if (this.selectedAudioDeviceId) {
      this.stopAudioStream();
      this.hasDetectedMicrophone = true; // Reset detection state
      try {
        this.audioStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: this.selectedAudioDeviceId } },
          video: false
        });
        this.audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        const source = this.audioContext.createMediaStreamSource(
          this.audioStream
        );
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048; // Use a larger fftSize for more accurate RMS
        source.connect(this.analyser);
        this.updateAudioLevel();
      } catch (e) {
        this.audioLevel = 0;
      }
    }
  }

  stopVideoStream() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = null;
      if (this.liveCameraElement && this.liveCameraElement.nativeElement) {
        this.liveCameraElement.nativeElement.srcObject = null;
      }
    }
  }

  private updateAudioLevel() {
    if (!this.analyser) return;

    this.ngZone.runOutsideAngular(() => {
      const bufferLength = this.analyser.fftSize;
      const dataArray = new Uint8Array(bufferLength);
      this.analyser.getByteTimeDomainData(dataArray);
      // Calculate RMS (root mean square) for volume
      let sumSquares = 0;
      for (let i = 0; i < bufferLength; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }
      const rms = Math.sqrt(sumSquares / bufferLength);
      // Use a more sensitive scaling for UI
      const newAudioLevel = Math.min(1, rms * 6); // Increase multiplier for more visible movement
      if (newAudioLevel !== this.audioLevel) {
        this.audioLevel = newAudioLevel;
        this.ngZone.run(() => {
          this.cdr.detectChanges();
        });
      }
      this.audioLevelAnimationFrame = requestAnimationFrame(() =>
        this.updateAudioLevel()
      );
      if (rms > 0.1) {
        this.hasDetectedMicrophone = true;
        this.cameraError = null;
      }
    });
  }

  onSubmitClick(): void {
    if (this.currentState === CurrentState.Guidelines) {
      if (!this.hasGivenGuidelinesAnswer) {
        this.guidelinesError = this.translate.instant(
          'videoOpenEnded.guidelinesRequired'
        );
        return;
      } else if (this.hasGivenGuidelinesAnswer && !this.hasAgreedToGuidelines) {
        // User disagrees with guidelines - behave like skip button
        const mockEvent = { preventDefault: () => {} };
        this.onFormSkip(mockEvent);
        return;
      }
      // Check if permissions have already been granted
      navigator.permissions
        .query({ name: 'camera' as PermissionName })
        .then(cameraPerm => {
          navigator.permissions
            .query({ name: 'microphone' as PermissionName })
            .then(micPerm => {
              if (
                cameraPerm.state === 'granted' &&
                micPerm.state === 'granted'
              ) {
                this.hasMediaPermissions = true;
                this.loadDevices().then(() => {
                  this.currentState = CurrentState.ChooseDevices;
                  this.onVideoDeviceChange();
                  this.onAudioDeviceChange();
                  this.cdr.detectChanges();
                });
              } else {
                this.currentState = CurrentState.Permissions;
                this.cdr.detectChanges();
              }
            })
            .catch(() => {
              // Fallback if permissions API is not available
              this.currentState = CurrentState.Permissions;
              this.cdr.detectChanges();
            });
        })
        .catch(() => {
          // Fallback if permissions API is not available
          this.currentState = CurrentState.Permissions;
          this.cdr.detectChanges();
        });
      return;
    } else if (this.currentState === CurrentState.ChooseDevices) {
      // Ensure a video device is selected

      if (!this.selectedVideoDeviceId || !this.selectedAudioDeviceId) {
        this.cameraError = this.translate.instant(
          'videoOpenEnded.chooseDevicesRequired'
        );
        return;
      }

      // Ensure an audio device is verified
      if (!this.hasDetectedMicrophone) {
        this.cameraError = this.translate.instant(
          'videoOpenEnded.microphoneVerificationRequired'
        );
        return;
      }
      // Proceed to record state
      this.goToRecordState();
      this.cdr.detectChanges();
    }
    // ...existing code for other states...
  }

  onFormSkip(event): void {
    this.formSkip.emit(event);
  }

  onFormSubmit(event: any): void {
    this.formSubmit.emit({ ...event, blob: this.blob });
  }

  // Example method to switch state
  async goToRecordState() {
    this.currentState = CurrentState.Record;
    let modalSettings: any = {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'video-recorder-modal'
    };
    if (this.isPreviewApp) {
      modalSettings = {
        ...modalSettings,
        container: '#injectedModals'
      };
    }
    const modalRef = this.modalService.open(
      VideoRecorderComponent,
      modalSettings
    );

    modalRef.componentInstance.question = this.question;
    modalRef.componentInstance.isPreview = this.isPreviewApp;

    try {
      const result = await modalRef.result;
      if (this.isPreviewApp) {
        this.isReadyToSubmit = true;
        this.goToReviewState();
        return;
      }

      if (this.isBlob(result)) {
        this.goToReviewState();
        this.blob = result;
        this.downloadUrl = window.URL.createObjectURL(result);
        if (this.reviewVideoPlayer?.nativeElement) {
          this.reviewVideoPlayer.nativeElement.src = this.downloadUrl;
        }
        this.isReadyToSubmit = true;
        this.safeDetectChanges();
      } else {
        this.resetReviewState();
      }
    } catch (e) {
      this.resetReviewState();
    }
  }

  private resetReviewState() {
    this.isReadyToSubmit = false;
    this.blob = null;
    this.downloadUrl = '';
    this.currentState = CurrentState.ChooseDevices;
    this.loadDevices();
    this.startCameraPreview();
    this.safeDetectChanges();
  }

  startCameraPreview() {
    if (!this.selectedVideoDeviceId) return;
    this.stopVideoStream();
    navigator.mediaDevices
      .getUserMedia({
        video: { deviceId: { exact: this.selectedVideoDeviceId } },
        audio: false
      })
      .then(stream => {
        this.videoStream = stream;
        if (this.liveCameraElement && this.liveCameraElement.nativeElement) {
          this.liveCameraElement.nativeElement.srcObject = stream;
          this.liveCameraElement.nativeElement.play();
        }
      })
      .catch(() => {
        this.cameraError = this.translate.instant(
          'videoOpenEnded.cameraNotDetected'
        );
        this.safeDetectChanges();
      });
  }

  goToReviewState() {
    this.currentState = CurrentState.Review;
  }

  get submitButtonClasses() {
    const isReview = this.currentState === CurrentState.Review;
    return {
      primarySection: isReview ? !this.isLoading && !this.isSuccess : true,
      loadingSection: isReview ? this.isLoading : false,
      successSection: isReview ? this.isSuccess : false
    };
  }

  get submitButtonLabel(): string {
    // Show "Continue" if guidelines or chooseDevices
    if (
      this.currentState === CurrentState.Guidelines ||
      this.currentState === CurrentState.ChooseDevices
    ) {
      return this.translate.instant('general.continue');
    }
    // If not special mission, show "Continue" unless it's Review, then "Submit"
    if (!this.isSpecialMission) {
      if (this.currentState === CurrentState.Review) {
        return this.translate.instant('general.submit');
      }
      return this.translate.instant('general.continue');
    }
    // For special mission, always show "Continue"
    return this.translate.instant('general.continue');
  }

  onDeleteReviewVideo() {
    this.blob = null;
    this.downloadUrl = '';
    if (this.reviewVideoPlayer && this.reviewVideoPlayer.nativeElement) {
      this.reviewVideoPlayer.nativeElement.src = '';
    }
    this.isReadyToSubmit = false;
    this.isReviewVideoPlaying = false;
    this.currentState = CurrentState.ChooseDevices; // Go back to record state
    this.resetReviewState();
  }

  onRetakeReviewVideo() {
    this.goToRecordState();
    if (this.reviewVideoPlayer && this.reviewVideoPlayer.nativeElement) {
      this.reviewVideoPlayer.nativeElement.src = '';
    }
    this.isReviewVideoPlaying = false;
    this.blob = null;
    this.downloadUrl = '';
    this.isReadyToSubmit = false;
  }

  onReviewVideoPlay() {
    const videoPlayerModalRef = this.modalService.open(VideoPlayerComponent);
    videoPlayerModalRef.componentInstance.url = this.downloadUrl;
  }

  // Example: Use markForCheck for async UI updates
  private safeDetectChanges() {
    this.cdr.detectChanges();
  }

  // Example: Use type guard for modal result
  private isBlob(obj: any): obj is Blob {
    return obj instanceof Blob;
  }
}
